import { ApiProvider } from '@/provider/ApiProvider';
import { getTabAssignment } from '@/auth/activeAssignment';

import { type Refusal, toRefusal } from './refusals';
import type { ClientStatus, ClientStatusFields } from './clientStatus';
import type {
  AdminAssignment,
  AssignableRole,
  AdminUserDetail,
  AssignmentInput,
  CheckResult,
  ClientStatusInput,
  ContextType,
  EffectiveAccess,
  ManagedOrganisation,
  OrganisationInput,
  OrganisationOption,
  OrganisationType,
  OrganisationValue,
  OwnProfile,
  UserOrganisation,
  UserListPage,
} from './types';

/**
 * Calls of the user and role-assignment administration on the identity
 * service (`/identity/api/admin/...`, `/identity/api/me/...`), through
 * ApiProvider.identityRequest: same token handling and
 * X-PNET-Expected-Assignment as every other identity call (part 4).
 */

export type Result<T> = { ok: true; data: T; message: string } | { ok: false; refusal: Refusal };

const unwrap = <T>(payload: unknown): Result<T> => {
  const refusal = toRefusal(payload);
  if (refusal) return { ok: false, refusal };
  const body = payload as { data?: T; message?: unknown };
  return { ok: true, data: body.data as T, message: String(body.message ?? '') };
};

/** Answers that are not wrapped in {success, data} (me/effective-access). */
const unwrapRaw = <T>(
  payload: unknown,
  isBody: (p: Record<string, unknown>) => boolean,
): Result<T> => {
  if (payload && typeof payload === 'object' && isBody(payload as Record<string, unknown>))
    return { ok: true, data: payload as T, message: '' };
  return { ok: false, refusal: toRefusal(payload ?? null) as Refusal };
};

export interface UserQuery {
  search?: string;
  status?: '' | '0' | '1';
  page?: number;
  perPage?: number;
  /** only the users of this organisation (access context); from an access review link */
  contextId?: number | null;
  /**
   * members, guests, invitations or all of them (owner 2026-10-16); absent: the list as
   * before the unified page (an older server ignores it)
   */
  membership?: 'member' | 'guest' | 'invited' | 'all' | null;
  /** internal callers only: people of this type */
  contextType?: ContextType | null;
}

export async function listUsers(q: UserQuery = {}): Promise<Result<UserListPage>> {
  const params = new URLSearchParams();
  if (q.search && q.search.trim() !== '') params.set('search', q.search.trim());
  if (q.status === '0' || q.status === '1') params.set('status', q.status);
  if (q.contextId) params.set('context_id', String(q.contextId));
  if (q.membership) params.set('membership', q.membership);
  if (q.contextType) params.set('context_type', q.contextType);
  params.set('page', String(q.page ?? 1));
  params.set('per_page', String(q.perPage ?? 25));
  return unwrap(await ApiProvider.identityRequest('GET', `admin/users?${params.toString()}`));
}

export async function getUser(id: number): Promise<Result<AdminUserDetail>> {
  return unwrap(await ApiProvider.identityRequest('GET', `admin/users/${id}`));
}

export async function createUser(body: {
  first_name: string;
  last_name: string;
  email: string;
  status: number;
  /** the person's type: Internal (1) only; a client organisation's people are invited (owner 2026-10-31) */
  type: number;
  assignment: AssignmentInput;
}): Promise<Result<AdminUserDetail>> {
  return unwrap(await ApiProvider.identityRequest('POST', 'admin/users', body));
}

export async function updateUser(
  id: number,
  body: Record<string, unknown>,
): Promise<Result<AdminUserDetail>> {
  return unwrap(await ApiProvider.identityRequest('PUT', `admin/users/${id}`, body));
}

export async function setUserStatus(
  id: number,
  status: 0 | 1,
): Promise<Result<{ id: number; status: number }>> {
  return unwrap(await ApiProvider.identityRequest('PUT', `admin/users/${id}/status`, { status }));
}

/**
 * PUT admin/users/{id}/home, "Correct home organisation" (owner 2026-10-31): a Super
 * Admin sets the person's home and type together, with a reason. `context_id` is a client
 * organisation that is not a former client, or null for Internal (ASI staff, company
 * e-mail only). Refused 409 user_has_running_roles while any role still runs; creates no
 * role, takes no seat; signs the person out everywhere. It replaces PUT .../type and PUT
 * .../organisation (both answer 410 use_correct_home).
 */
export async function correctUserHome(
  id: number,
  body: { context_id: number | null; reason: string },
): Promise<Result<{ id: number; type: number | null; organisation: UserOrganisation | null }>> {
  return unwrap(await ApiProvider.identityRequest('PUT', `admin/users/${id}/home`, body));
}

/**
 * GET admin/organisations?type=&search=: organisations of a customer type
 * with at least one active role (tenant admins: only their own).
 */
export async function listOrganisations(
  type: OrganisationType,
  search = '',
): Promise<Result<OrganisationOption[]>> {
  const params = new URLSearchParams({ type });
  if (search.trim() !== '') params.set('search', search.trim());
  const r = unwrap<{ organisations?: OrganisationOption[] }>(
    await ApiProvider.identityRequest('GET', `admin/organisations?${params.toString()}`),
  );
  if (!r.ok) return r;
  return {
    ok: true,
    data: Array.isArray(r.data?.organisations) ? r.data.organisations : [],
    message: r.message,
  };
}

// ---- organisations and their client status (owner decision 2026-10-07) -----------------------

export interface ManagedOrganisationQuery {
  type?: OrganisationType | '';
  clientStatus?: ClientStatus | '';
  search?: string;
  contextId?: number | null;
  /** 1-based */
  page?: number;
  perPage?: number;
}

export interface ManagedOrganisationPage {
  organisations: ManagedOrganisation[];
  total: number;
  page: number;
  per_page: number;
}

/**
 * GET admin/organisations?manage=1: Settings > Manage Users > Organisations
 * (platform owners and Application Admins; others 403
 * organisation_admin_required).
 */
export async function listManagedOrganisations(
  q: ManagedOrganisationQuery = {},
): Promise<Result<ManagedOrganisationPage>> {
  const params = new URLSearchParams({ manage: '1' });
  if (q.type) params.set('type', q.type);
  if (q.clientStatus) params.set('client_status', q.clientStatus);
  if (q.search && q.search.trim() !== '') params.set('search', q.search.trim());
  if (q.contextId) params.set('context_id', String(q.contextId));
  if (q.page) params.set('page', String(q.page));
  if (q.perPage) params.set('per_page', String(q.perPage));
  const r = unwrap<Partial<ManagedOrganisationPage>>(
    await ApiProvider.identityRequest('GET', `admin/organisations?${params.toString()}`),
  );
  if (!r.ok) return r;
  const rows = Array.isArray(r.data?.organisations) ? r.data.organisations : [];
  return {
    ok: true,
    data: {
      organisations: rows,
      total: Number(r.data?.total ?? rows.length),
      page: Number(r.data?.page ?? q.page ?? 1),
      per_page: Number(r.data?.per_page ?? q.perPage ?? rows.length),
    },
    message: r.message,
  };
}

/** POST admin/organisations: a customer organisation with its client status. */
export async function createOrganisation(
  body: {
    type: OrganisationType;
    brand_id?: number;
    property_id?: number;
  } & ClientStatusInput,
): Promise<Result<ManagedOrganisation>> {
  return unwrap(
    await ApiProvider.identityRequest('POST', 'admin/organisations', { ...body } as Record<
      string,
      unknown
    >),
  );
}

/**
 * The client status of one organisation (the chip of pages whose data comes
 * from elsewhere, e.g. Login Preferences): the Organisations list for
 * platform owners and Application Admins; otherwise the caller's own
 * organisation from effective-access (an organisation admin). null: unknown.
 */
export async function organisationStatusOf(contextId: number): Promise<ClientStatusFields | null> {
  // the Organisations list is asked in an INTERNAL active role only: in a customer role
  // the organisation is the caller's own (effective-access below), and the list
  // refuses an organisation's admin (403 organisation_admin_required)
  if (getTabAssignment()?.contextType === 'INTERNAL') {
    const managed = await listManagedOrganisations({ contextId, perPage: 1 });
    if (managed.ok)
      return managed.data.organisations.find((o) => o.context_id === contextId) ?? null;
  }
  const own = await getEffectiveAccess();
  if (own.ok && Number(own.data.context?.id) === contextId) return own.data.context;
  return null;
}

/**
 * GET admin/organisation-values: the values the organisation's own dimension
 * may take in a combination (brand: the brand + sub-brands; right holder: the
 * partner + child partners; agency: the agency brand + its agency
 * sub-brands). The tenant by `context_id`, or type + brand / partner while it
 * has no context yet.
 */
export async function getOrganisationValues(
  tenant: {
    context_id?: number | null;
    type?: string;
    brand_id?: number | null;
    property_id?: number | null;
  },
  dimension: 'brand' | 'partner',
): Promise<Result<OrganisationValue[]>> {
  const params = new URLSearchParams();
  if (tenant.context_id) params.set('context_id', String(tenant.context_id));
  else {
    if (tenant.type) params.set('context_type', tenant.type);
    if (tenant.brand_id) params.set('brand_id', String(tenant.brand_id));
    if (tenant.property_id) params.set('property_id', String(tenant.property_id));
  }
  params.set('dimension', dimension);
  const r = unwrap<{ values?: OrganisationValue[] }>(
    await ApiProvider.identityRequest('GET', `admin/organisation-values?${params.toString()}`),
  );
  if (!r.ok) return r;
  return { ok: true, data: Array.isArray(r.data?.values) ? r.data.values : [], message: r.message };
}

export async function resetUserMfa(id: number): Promise<Result<{ id: number }>> {
  return unwrap(await ApiProvider.identityRequest('POST', `admin/users/${id}/mfa-reset`, {}));
}

export async function createAssignment(
  userId: number,
  body: AssignmentInput,
): Promise<Result<AdminAssignment>> {
  return unwrap(
    await ApiProvider.identityRequest(
      'POST',
      `admin/users/${userId}/assignments`,
      body as Record<string, unknown>,
    ),
  );
}

export async function updateAssignment(
  userId: number,
  assignmentId: number,
  body: AssignmentInput,
): Promise<Result<AdminAssignment>> {
  return unwrap(
    await ApiProvider.identityRequest(
      'PUT',
      `admin/users/${userId}/assignments/${assignmentId}`,
      body as Record<string, unknown>,
    ),
  );
}

export async function disableAssignment(
  userId: number,
  assignmentId: number,
): Promise<Result<AdminAssignment>> {
  return unwrap(
    await ApiProvider.identityRequest(
      'DELETE',
      `admin/users/${userId}/assignments/${assignmentId}`,
    ),
  );
}

/** The caller's effective access of this tab's assignment (else the active one). */
export async function getEffectiveAccess(): Promise<Result<EffectiveAccess>> {
  const tab = getTabAssignment();
  const path = tab ? `me/effective-access?ra=${tab.id}` : 'me/effective-access';
  return unwrapRaw(await ApiProvider.identityRequest('GET', path), (p) =>
    Array.isArray(p.permissions),
  );
}

export async function getOwnProfile(): Promise<Result<OwnProfile>> {
  return unwrap(await ApiProvider.identityRequest('GET', 'me/profile'));
}

export async function updateOwnProfile(body: Record<string, unknown>): Promise<Result<OwnProfile>> {
  return unwrap(await ApiProvider.identityRequest('PUT', 'me/profile', body));
}

/**
 * Roles the caller may assign in a tenant: GET admin/roles (backend part 3c).
 * `context_id` when the tenant's access context is known (tenant admins,
 * existing assignments); otherwise the tenant itself (type + brand_id /
 * property_id) for a brand / right holder / agency that has no context yet.
 * The person the role is for: `target_user_id` (an existing user) or
 * `user_type` (1-4, a user being created); the server then offers only roles
 * that fit that user's type.
 */
export async function listAssignableRoles(
  tenant: {
    context_id?: number | null;
    type?: string;
    brand_id?: number | null;
    property_id?: number | null;
  },
  forUser: { target_user_id?: number | null; user_type?: number | null } = {},
): Promise<Result<AssignableRole[]>> {
  const params = new URLSearchParams();
  if (tenant.context_id) params.set('context_id', String(tenant.context_id));
  else {
    if (tenant.type) params.set('context_type', tenant.type);
    if (tenant.brand_id) params.set('brand_id', String(tenant.brand_id));
    if (tenant.property_id) params.set('property_id', String(tenant.property_id));
  }
  if (forUser.target_user_id) params.set('target_user_id', String(forUser.target_user_id));
  else if (forUser.user_type) params.set('user_type', String(forUser.user_type));
  const r = unwrap<unknown>(
    await ApiProvider.identityRequest('GET', `admin/roles?${params.toString()}`),
  );
  if (!r.ok) return r;
  const data = r.data as { roles?: unknown } | unknown[] | null;
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { roles?: unknown })?.roles)
      ? (data as { roles: unknown[] }).roles
      : [];
  const roles: AssignableRole[] = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const x = row as Record<string, unknown>;
    const id = Number(x.id);
    if (!Number.isInteger(id) || id <= 0) continue;
    if (x.is_active === false || x.is_active === 0) continue;
    roles.push({
      id,
      name: String(x.name ?? `#${id}`),
      role_type: x.role_type == null ? null : Number(x.role_type),
    });
  }
  roles.sort((p, q) => p.name.localeCompare(q.name));
  return { ok: true, data: roles, message: r.message };
}

/** Who a pre-save check is for: an existing user, or a user being created. */
export type CheckTarget =
  | { userId: number }
  | {
      newUser: {
        user_type: number;
        email?: string | null;
        /** the new customer user's organisation, checked like POST admin/users */
        organisation?: OrganisationInput | null;
      };
    };

/**
 * The pre-save check (backend: POST admin/users/{id}/assignments/check, or
 * admin/assignments/check for a user being created): the same body as the
 * create / update of the assignment (+ `assignment_id` when editing). Runs
 * every validation of the save without writing; answers errors (block Save),
 * warnings, one sentence and live counts per combination.
 */
export async function checkAssignment(
  target: CheckTarget,
  body: AssignmentInput & { assignment_id?: number },
): Promise<Result<CheckResult>> {
  if ('userId' in target)
    return unwrap(
      await ApiProvider.identityRequest(
        'POST',
        `admin/users/${target.userId}/assignments/check`,
        body as Record<string, unknown>,
      ),
    );
  const { user_type, email, organisation } = target.newUser;
  return unwrap(
    await ApiProvider.identityRequest('POST', 'admin/assignments/check', {
      ...(body as Record<string, unknown>),
      user_type,
      ...(email ? { email } : {}),
      ...(organisation ? { organisation } : {}),
    }),
  );
}
